<template>
  <!-- No explicit Teleport here (unlike a v-dialog, which already portals
       itself) -- wrapping v-navigation-drawer in one broke Vuetify's own
       "translate off-screen when inactive" transform for it, because that
       calculation depends on the drawer sitting inside its normal v-app/
       v-layout ancestry. Teleporting it to <body> moved it outside that
       ancestry, so the drawer rendered fully visible regardless of
       modelValue -- confirmed live 2026-08-15: it opened on every page
       load and its own close button did nothing. The main left sidebar
       nav, which is NOT teleported, has always hidden/shown correctly. -->
  <v-navigation-drawer
    :model-value="modelValue"
    location="right"
    temporary
    :width="drawerWidth"
    class="ctx-drawer"
    @update:model-value="$emit('update:modelValue', $event)"
  >
      <div class="ctx-drawer__inner">
        <header class="ctx-drawer__header">
          <div class="ctx-drawer__header-lead">
            <span class="ctx-drawer__avatar" aria-hidden="true">
              <v-icon icon="mdi-tune-variant" size="22" />
            </span>
            <div class="ctx-drawer__header-copy">
              <h2 class="ctx-drawer__name">Lead defaults</h2>
              <p class="ctx-drawer__subtitle">
                Seed fields &amp; statuses for every new product
              </p>
            </div>
          </div>
          <button
            type="button"
            class="ctx-drawer__close"
            aria-label="Close lead defaults drawer"
            @click="$emit('update:modelValue', false)"
          >
            <v-icon icon="mdi-close" size="20" />
          </button>
        </header>

        <div class="ctx-drawer__body">
          <section class="ctx-drawer__card" aria-labelledby="org-defaults-info-heading">
            <div class="ctx-drawer__card-head">
              <span class="ctx-drawer__card-icon" aria-hidden="true">
                <v-icon icon="mdi-information-outline" size="18" />
              </span>
              <h3 id="org-defaults-info-heading" class="ctx-drawer__card-title">
                How defaults work
              </h3>
            </div>
            <p class="ctx-drawer__block-text">
              These defaults are copied into each <strong>newly created
              context</strong>. Existing products are never changed — manage
              those from the Products screen instead. When nothing is
              configured here, new products start from the platform baseline.
            </p>
          </section>

          <!-- Default fields -->
          <section class="ctx-drawer__card" aria-labelledby="org-defaults-fields-heading">
            <div class="ctx-drawer__card-head">
              <span class="ctx-drawer__card-icon" aria-hidden="true">
                <v-icon icon="mdi-form-textbox" size="18" />
              </span>
              <h3 id="org-defaults-fields-heading" class="ctx-drawer__card-title">
                Default fields
                <span class="ctx-drawer__card-count">{{ defaultFields.length }}</span>
              </h3>
            </div>

            <ul v-if="defaultFields.length" class="ctx-drawer__fields">
              <li
                v-for="field in defaultFields"
                :key="field.key"
                class="ctx-drawer__field"
                :class="{ 'ctx-drawer__field--disabled': !field.enabled }"
              >
                <span class="ctx-drawer__field-icon" aria-hidden="true">
                  <v-icon :icon="field.icon" size="16" />
                </span>
                <div class="ctx-drawer__field-copy">
                  <span class="ctx-drawer__field-name">
                    {{ field.label }}
                    <span v-if="field.isSystem" class="ctx-drawer__field-badge">
                      default
                    </span>
                    <span
                      v-if="field.isUniqueIdentifier"
                      class="ctx-drawer__field-badge ctx-drawer__field-badge--id"
                    >
                      identifier
                    </span>
                  </span>
                  <span class="ctx-drawer__field-type">
                    {{ field.type }}<template v-if="field.options">
                      · {{ field.options }}</template>
                  </span>
                </div>
                <div class="ctx-drawer__field-actions">
                  <button
                    v-if="!field.isUniqueIdentifier"
                    type="button"
                    class="ctx-drawer__field-action"
                    aria-label="Make default unique identifier"
                    title="New products match leads on this field (upsert)"
                    :disabled="savingField || removingField || !field.enabled"
                    @click="makeIdentifier(field)"
                  >
                    <v-icon icon="mdi-key-outline" size="15" />
                  </button>
                  <button
                    type="button"
                    class="ctx-drawer__field-delete"
                    aria-label="Delete field"
                    title="Delete default field"
                    :disabled="savingField || removingField"
                    @click="askRemoveField(field)"
                  >
                    <v-icon icon="mdi-trash-can-outline" size="15" />
                  </button>
                </div>
              </li>
            </ul>
            <p v-else class="ctx-drawer__empty">
              No org defaults configured — new products start with the
              platform's 7 standard fields. Add a field below to extend that
              baseline.
            </p>

            <div class="ctx-drawer__field-form">
              <span class="ctx-drawer__block-label">Add default field</span>
              <div class="ctx-drawer__field-form-row">
                <v-text-field
                  v-model="fieldForm.label"
                  placeholder="Field label, e.g. Budget Range"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="ctx-drawer__field-input"
                />
                <v-select
                  v-model="fieldForm.type"
                  :items="CONTEXT_FIELD_TYPE_OPTIONS"
                  variant="outlined"
                  density="compact"
                  hide-details
                  :menu-props="{ zIndex: 4000 }"
                  class="ctx-drawer__field-input ctx-drawer__field-input--type"
                />
              </div>
              <v-text-field
                v-if="fieldForm.type === 'DROPDOWN'"
                v-model="fieldForm.options"
                placeholder="Options, comma separated"
                variant="outlined"
                density="compact"
                hide-details
                class="ctx-drawer__field-input"
              />
              <div class="ctx-drawer__field-form-actions">
                <v-btn
                  variant="flat"
                  size="small"
                  class="ctx-drawer__field-save"
                  :loading="savingField"
                  :disabled="!String(fieldForm.label).trim()"
                  @click="saveFieldDefinition"
                >
                  Add field
                </v-btn>
              </div>
            </div>
          </section>

          <!-- Default lead statuses -->
          <section class="ctx-drawer__card" aria-labelledby="org-defaults-statuses-heading">
            <div class="ctx-drawer__card-head">
              <span class="ctx-drawer__card-icon" aria-hidden="true">
                <v-icon icon="mdi-list-status" size="18" />
              </span>
              <h3 id="org-defaults-statuses-heading" class="ctx-drawer__card-title">
                Default lead statuses
                <span class="ctx-drawer__card-count">{{ defaultStatuses.length }}</span>
              </h3>
            </div>

            <p class="ctx-drawer__block-label ctx-drawer__statuses-hint">
              New products start with this status list. Each product can
              still customise its own list afterwards.
            </p>

            <div v-if="defaultStatuses.length" class="ctx-drawer__statuses">
              <span
                v-for="status in defaultStatuses"
                :key="status"
                class="ctx-drawer__status-chip"
              >
                {{ status }}
                <button
                  v-if="defaultStatuses.length > 1"
                  type="button"
                  class="ctx-drawer__status-remove"
                  :aria-label="`Remove status ${status}`"
                  :disabled="statusWorking"
                  @click="removeStatus(status)"
                >
                  <v-icon icon="mdi-close" size="12" />
                </button>
              </span>
            </div>
            <p v-else class="ctx-drawer__empty">
              No org defaults configured — new products start with PENDING,
              CONTACTED, CONVERTED and REJECTED.
            </p>

            <div class="ctx-drawer__field-form-row ctx-drawer__status-form">
              <v-text-field
                v-model="newStatus"
                placeholder="New status, e.g. FOLLOW_UP"
                variant="outlined"
                density="compact"
                hide-details
                class="ctx-drawer__field-input"
                @keydown.enter.prevent="addStatus"
              />
              <v-btn
                variant="flat"
                size="small"
                class="ctx-drawer__field-save"
                :loading="statusWorking"
                :disabled="!String(newStatus).trim()"
                @click="addStatus"
              >
                Add
              </v-btn>
            </div>
          </section>
        </div>
      </div>
    </v-navigation-drawer>

    <v-dialog
      v-model="showRemoveConfirm"
      max-width="440"
      :z-index="4000"
      persistent
    >
      <div class="ctx-confirm">
        <div class="ctx-confirm__glow" aria-hidden="true"></div>

        <div class="ctx-confirm__head">
          <span class="ctx-confirm__icon" aria-hidden="true">
            <v-icon icon="mdi-trash-can-outline" size="22" />
          </span>
          <div class="ctx-confirm__head-copy">
            <span class="ctx-confirm__eyebrow">Remove default field</span>
            <h3 class="ctx-confirm__title">Delete this field?</h3>
          </div>
          <button
            type="button"
            class="ctx-confirm__close"
            aria-label="Close"
            :disabled="removingField"
            @click="showRemoveConfirm = false"
          >
            <v-icon icon="mdi-close" size="18" />
          </button>
        </div>

        <div class="ctx-confirm__body">
          <div class="ctx-confirm__field-chip">
            <v-icon :icon="fieldToRemove?.icon || 'mdi-format-text'" size="16" />
            <span class="ctx-confirm__field-name">{{ fieldToRemove?.label }}</span>
            <span class="ctx-confirm__field-type">{{ fieldToRemove?.type }}</span>
          </div>
          <p class="ctx-confirm__text">
            This field is removed from the organisation defaults only —
            products that already exist keep their own field registry and are
            not affected.
          </p>
        </div>

        <div class="ctx-confirm__actions">
          <button
            type="button"
            class="ctx-confirm__btn ctx-confirm__btn--ghost"
            :disabled="removingField"
            @click="showRemoveConfirm = false"
          >
            Keep field
          </button>
          <button
            type="button"
            class="ctx-confirm__btn ctx-confirm__btn--danger"
            :disabled="removingField"
            @click="confirmRemoveField"
          >
            <v-progress-circular
              v-if="removingField"
              indeterminate
              size="14"
              width="2"
            />
            <v-icon v-else icon="mdi-trash-can-outline" size="16" />
            {{ removingField ? 'Deleting…' : 'Delete field' }}
          </button>
        </div>
      </div>
    </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import {
  addOrgDefaultFields,
  removeOrgDefaultFields,
  addOrgDefaultLeadStatuses,
  removeOrgDefaultLeadStatuses,
} from '@/api/orgDefaultsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { normalizeContextFieldDefinitions, CONTEXT_FIELD_TYPE_OPTIONS } from '@/utils/contexts'
import '@/styles/contexts/info-drawer.css'

defineOptions({ name: 'OrgDefaultsDrawer' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  // The organisation record — reads default_field_definitions and
  // default_lead_statuses from it.
  organization: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['update:modelValue', 'saved', 'error'])

const display = useDisplay()
const drawerWidth = computed(() =>
  display.mobile.value ? '100%' : Math.min(520, display.width.value),
)

const FIELD_TYPE_ICONS = {
  TEXT: 'mdi-format-text',
  NUMBER: 'mdi-numeric',
  DROPDOWN: 'mdi-form-dropdown',
  PHONE_NUMBER: 'mdi-phone-outline',
  DATE: 'mdi-calendar-outline',
  TIME: 'mdi-clock-outline',
  EMAIL: 'mdi-email-outline',
}

const fieldForm = ref({ label: '', type: 'TEXT', options: '' })
const savingField = ref(false)
const showRemoveConfirm = ref(false)
const fieldToRemove = ref(null)
const removingField = ref(false)

// Local overrides let the drawer consume mutation responses (or apply
// optimistic updates) without waiting for an org-profile refresh.
const definitionsOverride = ref(null)
const statusesOverride = ref(null)

const newStatus = ref('')
const statusWorking = ref(false)

function parseDefinitions(raw) {
  let value = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      value = null
    }
  }
  return normalizeContextFieldDefinitions(value)
}

const currentDefinitions = computed(
  () =>
    definitionsOverride.value ??
    parseDefinitions(props.organization?.default_field_definitions),
)

const defaultFields = computed(() =>
  Object.entries(currentDefinitions.value).map(([key, definition]) => {
    const type = String(definition?.type ?? 'TEXT').toUpperCase()
    const optionsList = Array.isArray(definition?.options)
      ? definition.options.filter(Boolean)
      : []
    return {
      key,
      label: String(definition?.label ?? '').trim() || key,
      type,
      icon: FIELD_TYPE_ICONS[type] || FIELD_TYPE_ICONS.TEXT,
      options: optionsList.join(', '),
      optionsList,
      enabled: definition?.enabled !== false,
      isSystem: Boolean(definition?.is_system),
      locked: Boolean(definition?.locked),
      isUniqueIdentifier: Boolean(definition?.is_unique_identifier),
    }
  }),
)

const defaultStatuses = computed(() => {
  const source =
    statusesOverride.value ??
    (Array.isArray(props.organization?.default_lead_statuses)
      ? props.organization.default_lead_statuses
      : [])
  return source.map((status) => String(status ?? '').trim()).filter(Boolean)
})

function emitSaved(message) {
  emit('saved', {
    message,
    definitions: definitionsOverride.value,
    statuses: statusesOverride.value,
  })
}

/**
 * addOrgDefaultFields overwrites each named field's whole definition, so
 * edits resend every row's full current state — otherwise omitted flags
 * (like is_unique_identifier) are silently cleared server-side.
 */
function buildFullRows(overrides = {}) {
  return defaultFields.value.map((field) => {
    const override = overrides[field.key] || {}
    const type = override.type ?? field.type
    const row = {
      label: override.label ?? field.label,
      key: field.key,
      type,
      enabled: override.enabled ?? field.enabled,
      is_unique_identifier:
        override.is_unique_identifier ?? field.isUniqueIdentifier,
    }
    const options = override.options ?? field.optionsList
    if (type === 'DROPDOWN' && options.length) {
      row.options = options
    }
    return row
  })
}

/** Applies the mutation response registry (or an optimistic fallback). */
function applyDefinitionsResponse(response, fallbackFn) {
  const returned =
    response?.data?.default_field_definitions ??
    response?.data?.custom_field_definitions ??
    response?.data?.field_definitions
  if (returned && typeof returned === 'object') {
    definitionsOverride.value = normalizeContextFieldDefinitions(returned)
  } else if (typeof fallbackFn === 'function') {
    definitionsOverride.value = fallbackFn({ ...currentDefinitions.value })
  }
}

async function submitFieldRows(rows, successFallback, fallbackFn) {
  savingField.value = true
  try {
    const response = await addOrgDefaultFields({ field_definitions: rows })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to save default fields'))
      return false
    }
    applyDefinitionsResponse(response, fallbackFn)
    emitSaved(getApiMessage(response, successFallback))
    return true
  } finally {
    savingField.value = false
  }
}

function rowsToDefinitions(rows) {
  const map = {}
  for (const row of rows) {
    const key =
      row.key ||
      String(row.label).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')
    map[key] = {
      label: row.label,
      type: row.type,
      ...(row.options ? { options: row.options } : {}),
      enabled: row.enabled !== false,
      is_unique_identifier: Boolean(row.is_unique_identifier),
    }
  }
  return map
}

async function makeIdentifier(field) {
  if (field.isUniqueIdentifier) return
  if (!field.enabled) {
    emit('error', 'Enable the field before making it the default identifier.')
    return
  }
  const overrides = {}
  defaultFields.value.forEach((entry) => {
    overrides[entry.key] = { is_unique_identifier: entry.key === field.key }
  })
  const rows = buildFullRows(overrides)
  await submitFieldRows(
    rows,
    `"${field.label}" is now the default identifier.`,
    () => rowsToDefinitions(rows),
  )
}

function resetFieldForm() {
  fieldForm.value = { label: '', type: 'TEXT', options: '' }
}

async function saveFieldDefinition() {
  const label = String(fieldForm.value.label ?? '').trim()
  const type = String(fieldForm.value.type ?? 'TEXT').toUpperCase()
  const options = String(fieldForm.value.options ?? '')
    .split(',')
    .map((option) => option.trim())
    .filter(Boolean)

  if (!label) return
  if (type === 'DROPDOWN' && !options.length) {
    emit('error', 'Dropdown fields need at least one option.')
    return
  }

  // Only named fields are overwritten server-side, so a new field can be
  // sent on its own without touching the existing rows.
  const newRow = { label, type, ...(type === 'DROPDOWN' ? { options } : {}), enabled: true }
  const rows = [newRow]

  const saved = await submitFieldRows(
    rows,
    'Default field added successfully.',
    () => ({ ...currentDefinitions.value, ...rowsToDefinitions(rows) }),
  )
  if (saved) resetFieldForm()
}

function askRemoveField(field) {
  fieldToRemove.value = field
  showRemoveConfirm.value = true
}

async function confirmRemoveField() {
  if (!fieldToRemove.value) return

  removingField.value = true
  try {
    const removedKey = fieldToRemove.value.key
    const response = await removeOrgDefaultFields({ field_names: [removedKey] })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to delete default field'))
      return
    }

    const next = { ...currentDefinitions.value }
    delete next[removedKey]
    definitionsOverride.value = next

    showRemoveConfirm.value = false
    fieldToRemove.value = null
    emitSaved(getApiMessage(response, 'Default field deleted.'))
  } finally {
    removingField.value = false
  }
}

function applyStatusesResponse(response, fallbackList) {
  const returned =
    response?.data?.statuses ?? response?.data?.default_lead_statuses
  statusesOverride.value = Array.isArray(returned) ? returned : fallbackList
}

async function addStatus() {
  const status = String(newStatus.value ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
  if (!status) return

  statusWorking.value = true
  try {
    const response = await addOrgDefaultLeadStatuses({ statuses: [status] })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to add default status'))
      return
    }
    applyStatusesResponse(response, [
      ...new Set([...defaultStatuses.value, status]),
    ])
    newStatus.value = ''
    emitSaved(getApiMessage(response, `Default status "${status}" added.`))
  } finally {
    statusWorking.value = false
  }
}

async function removeStatus(status) {
  statusWorking.value = true
  try {
    const response = await removeOrgDefaultLeadStatuses({ statuses: [status] })
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to remove default status'))
      return
    }
    applyStatusesResponse(
      response,
      defaultStatuses.value.filter((item) => item !== status),
    )
    emitSaved(getApiMessage(response, `Default status "${status}" removed.`))
  } finally {
    statusWorking.value = false
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return
    definitionsOverride.value = null
    statusesOverride.value = null
    resetFieldForm()
    showRemoveConfirm.value = false
    fieldToRemove.value = null
    newStatus.value = ''
  },
)
</script>
